import React, { Suspense, useEffect, useMemo, useCallback } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Toaster } from "react-hot-toast";

// Styles
import "./App.css";

// Global Components
import ScrollToTop from "./hooks/ScrollToTop";
import SyncCartToProducts from "./hooks/useSyncCartToProducts";
import StripeCleanup from "./components/StripeCleanup";
import PageLoader from "./shared/loader/PageLoader";

// Redux Store
import { selectToken, selectUser } from "./store/reducer/authSlice";
import { fetchCoreSettings, fetchUserProfile } from "./store/thunk/authThunk";
import { fetchCartInfo } from "./store/thunk/cartThunk";

// Routes Configuration
import createRoutes from "./routes";

// Toast notification configuration
const TOAST_CONFIG = {
  position: "top-center",
  reverseOrder: false,
  toastOptions: {
    duration: 4000,
    style: {
      background: "#363636",
      color: "#fff",
      borderRadius: "8px",
      fontSize: "14px",
    },
    success: {
      duration: 3000,
      iconTheme: {
        primary: "#4aed88",
      },
    },
    error: {
      duration: 5000,
      iconTheme: {
        primary: "#ff4b4b",
      },
    },
    loading: {
      duration: Infinity,
    },
  },
};

function App() {
  // Hooks
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux State
  const token = useSelector(selectToken);
  const user = useSelector(selectUser);

  // Memoized routes configuration
  const routes = useMemo(() => createRoutes(), []);

  // Optimized user data loading function
  const loadUserData = useCallback(async (userData) => {
    try {
      const promises = [];
      if (userData?.data?.roles !== 1) {
        promises.push(dispatch(fetchCartInfo()));
      }

      // Load user-specific data in background (non-blocking)
      const results = await Promise.allSettled(promises);

      // Log any failures for debugging
      const failures = results.filter(result => result.status === "rejected");
      if (failures.length > 0) {
        console.warn("Some user data failed to load:", failures);
      } else {
        // data loaded successfully");
      }
    } catch (error) {
      console.error("Error loading user data:", error);
    }
  }, [dispatch]);

  // Optimized global data loading function
  const loadGlobalData = useCallback(async () => {
    try {
      await Promise.all([
        dispatch(fetchCoreSettings()),
      ]);
    //   console.log("Global data loaded successfully");
    } catch (error) {
      console.error("Failed to load some global data:", error);
    }
  }, [dispatch]);

  // Authentication and user validation effect
  useEffect(() => {
    if (token && !user) {
      //console.log("Token exists, validating user...");

      dispatch(fetchUserProfile())
        .unwrap()
        .then((userData) => {
          //console.log("User validation successful");
          // Load user-specific data after successful validation
          loadUserData(userData);
        })
        .catch((error) => {
        //   console.log("Token validation failed, redirecting:", error);
          navigate("/signin");
        });
    }
  }, [token, user, dispatch, navigate, loadUserData]);

  // Global application data loading effect (runs once)
  useEffect(() => {
    loadGlobalData();
  }, [loadGlobalData]);

  // Memoized route components for performance
  const routeComponents = useMemo(() =>
    routes.map((route, index) => (
      <Route key={route.path || index} path={route.path} element={route.element} />
    )),
    [routes]
  );

  return (
    <Suspense fallback={<PageLoader />}>
      {/* Global Components - Always rendered */}
      <ScrollToTop />
      <SyncCartToProducts />
      <StripeCleanup />

      {/* Toast Notification System */}
      <Toaster {...TOAST_CONFIG} />

      {/* Application Routes */}
      <Routes>
        {routeComponents}
      </Routes>
    </Suspense>
  );
}

export default App;